import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, LineChart, Line } from "recharts";
import { API } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ORG_TYPE_LABELS } from "../lib/constants";

const COLORS = ["#009E60", "#3A75C4", "#FCD116", "#0B132B", "#10B981", "#64748B", "#F59E0B", "#EF4444", "#2d5fa3", "#00804e"];

function ChartCard({ title, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle className="text-base font-heading">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function Statistics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get("/statistics").then((r) => {
      const d = r.data;
      d.organizations_by_type = (d.organizations_by_type || []).map((o) => ({ ...o, name: ORG_TYPE_LABELS[o.name] || o.name }));
      setData(d);
    }).catch(() => setData({}));
  }, []);

  if (!data) {
    return <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-72 w-full" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-[#3A75C4]" /> Statistiques
        </h1>
        <p className="text-sm text-muted-foreground">Donnees agregees des referentiels ALARA.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Vehicules par marque" testid="chart-marques">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.vehicles_by_marque || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <RTooltip />
              <Bar dataKey="count" name="Vehicules" fill="#009E60" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Vehicules par type" testid="chart-types">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.vehicles_by_type || []} dataKey="count" nameKey="name" outerRadius={90} label>
                {(data.vehicles_by_type || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conducteurs par province" testid="chart-provinces">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.drivers_by_province || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
              <RTooltip />
              <Bar dataKey="count" name="Conducteurs" fill="#3A75C4" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Structures par categorie" testid="chart-structures">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.organizations_by_type || []} dataKey="count" nameKey="name" outerRadius={90} label>
                {(data.organizations_by_type || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Permis par categorie" testid="chart-permis">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.licenses_by_categorie || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <RTooltip />
              <Bar dataKey="count" name="Permis" fill="#FCD116" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Activite des recherches (14 jours)" testid="chart-recherches">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.searches_per_day || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="jour" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} width={35} allowDecimals={false} />
              <RTooltip />
              <Line type="monotone" dataKey="recherches" name="Recherches" stroke="#009E60" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
